// Cisco IOS console session: modes, prompts, command execution, syslog
// messages, "?" help, Tab completion and interactive confirmations.
//
// A session is bound to ONE device inside a shared network object. Commands
// mutate that device's state; the simulator (../net/sim.js) derives everything
// else (link state, routing, reachability) from it.

import { compile, match, help as helpFor, complete as completeFor, tokenize } from './parser.js';
import { parseIfName, parseIfRange, parseIfNameCreate } from './ifnames.js';
import { SW_SHOW, SW_CONFIG, SW_IF, R_IF, createInterface } from './switching.js';
import { WAN_SUFFIX, WAN_CONFIG, WAN_MODE_SPECS, WAN_IF, WAN_SHOW } from './wanCli.js';
import { DHCP_POOL_EXTRA, SVC_CONFIG, SVC_IF, SVC_SHOW, SVC_PRIV, fhrpSnapshot, fhrpLogs } from './servicesCli.js';
import { clockLine, logEvent } from '../net/services.js';
import { ROUTER_MODE_SPECS, ROUTER_SUFFIX, R_ROUTING_CONFIG, R_ROUTING_IF, R_ROUTING_SHOW, R_ROUTING_PRIV, adjSnapshot, adjLogs } from './routingCli.js';
import { isPo, isSubif, isSvi } from '../net/l2.js';
import * as show from './show.js';
import { SEC_SUFFIX, SEC_CONFIG, SEC_LINE, SEC_RADIUS, SEC_TACACS, SEC_STD_NACL, SEC_EXT_NACL, SEC_IF_ROUTER, SEC_IF_SWITCH, SEC_SHOW, SEC_USER_SHOW, SEC_PRIV } from './secCli.js';
import { mgmtOf, authenticate, loginPrompt, pwMatches, ensureMgmt } from '../net/security.js';
import { MGMT_PRIV, MGMT_CONFIG, MGMT_IF, ROMMON } from './mgmtCli.js';
import { chooseImage, ignoresStartup } from '../net/system.js';
import { aaaDeps, testAaa } from '../net/mgmtAccess.js';
import { dev, ifStatus, roundTrip, trace, isHost } from '../net/sim.js';
import {
  isIp, maskToPrefix, networkOf, inSubnet, isHostAddress, ipToInt, prefixToWildcard, v6Expand, v6Compress,
} from '../net/addr.js';

const MODE_SUFFIX = {
  user: '>', priv: '#', config: '(config)#', 'config-if': '(config-if)#', 'config-if-range': '(config-if-range)#',
  'config-vlan': '(config-vlan)#', 'config-dhcp': '(dhcp-config)#', ...ROUTER_SUFFIX, ...WAN_SUFFIX, ...SEC_SUFFIX,
};

// ------------------------------------------------------------- command specs
// Each spec: { pattern, kinds?:['router'|'switch'], run(s, args) → string[] | {lines, ask} }
const K = (kinds, specs) => specs.map((x) => ({ ...x, kinds }));
const ALL = null;

const SHOW_USER = [
  { pattern: 'show version', run: (s) => show.version(s.d, s.net) },
  { pattern: 'show ip interface brief', run: (s) => show.ipIntBrief(s.net, s.id) },
  { pattern: 'show interfaces', run: (s) => Object.keys(s.d.interfaces).flatMap((n) => show.showInterface(s.net, s.id, n)) },
  { pattern: 'show interfaces <IF>', run: (s, [i]) => show.showInterface(s.net, s.id, s.ifn(i)) },
  { pattern: 'show ip route', run: (s) => (s.d.kind === 'router' || s.d.ipRouting ? show.ipRoute(s.net, s.id) : [s.d.defaultGateway ? `Default gateway is ${s.d.defaultGateway}` : 'Default gateway is not set', '', 'Host               Gateway           Last Use    Total Uses  Interface', 'ICMP redirect cache is empty']) },
  { pattern: 'show arp', run: (s) => (s.d.kind === 'router' ? show.ipArp(s.net, s.id) : ['Protocol  Address          Age (min)  Hardware Addr   Type   Interface']) },
  { pattern: 'show ip arp', run: (s) => (s.d.kind === 'router' ? show.ipArp(s.net, s.id) : ['Protocol  Address          Age (min)  Hardware Addr   Type   Interface']) },
  { pattern: 'show cdp neighbors', run: (s) => show.cdpNeighbors(s.net, s.id) },
  { pattern: 'show history', run: (s) => s.state.history.slice(-10).map((h) => `  ${h}`) },
  { pattern: 'show clock', run: (s) => [clockLine(s.net, s.id)] },
  ...SEC_USER_SHOW,
  ...R_ROUTING_SHOW,
  ...K(['switch'], [
    { pattern: 'show interfaces status', run: (s) => show.interfacesStatus(s.net, s.id) },
    { pattern: 'show vlan brief', run: (s) => show.vlanBrief(s.net, s.id) },
    { pattern: 'show vlan', run: (s) => show.vlanBrief(s.net, s.id) },
    { pattern: 'show mac address-table', run: (s) => show.macTable(s.net, s.id) },
    { pattern: 'show mac address-table dynamic', run: (s) => show.macTable(s.net, s.id, { dynamicOnly: true }) },
    { pattern: 'show mac address-table interface <IF>', run: (s, [i]) => show.macTable(s.net, s.id, { iface: s.ifn(i), dynamicOnly: true }) },
  ]),
];
const SHOW_PRIV = [
  ...SHOW_USER,
  { pattern: 'show running-config', run: (s) => show.runningConfig(s.net, s.id) },
  { pattern: 'show running-config interface <IF>', run: (s, [i]) => show.runningConfig(s.net, s.id, s.ifn(i)) },
  { pattern: 'show startup-config', run: (s) => (s.d.startup ? ['Using 1823 out of 262136 bytes', ...s.d.startup.slice(3)] : ['startup-config is not present']) },
  ...SW_SHOW,
  ...WAN_SHOW,
  ...SVC_SHOW,
  ...SEC_SHOW,
  ...K(['router'], [
    { pattern: 'show ip nat translations', run: (s) => show.natTranslations(s.net, s.id) },
    { pattern: 'show ip dhcp binding', run: (s) => show.dhcpBinding(s.net, s.id) },
    { pattern: 'show ipv6 interface brief', run: (s) => show.ipv6IntBrief(s.net, s.id) },
  ]),
];


const EXEC_COMMON = [
  { pattern: 'ping <HOST>', run: (s, [h]) => s.ping(h) },
  { pattern: 'traceroute <HOST>', run: (s, [h]) => s.traceroute(h) },
  { pattern: 'exit', run: (s) => s.logout() },
  { pattern: 'logout', run: (s) => s.logout() },
];
const USER = [
  ...SHOW_USER, ...EXEC_COMMON,
  { pattern: 'enable', run: (s) => s.enable() },
];
const PRIV = [
  ...SHOW_PRIV, ...EXEC_COMMON,
  { pattern: 'enable', run: () => [] },
  { pattern: 'disable', run: (s) => { s.state.mode = 'user'; return []; } },
  { pattern: 'configure terminal', run: (s) => { s.state.mode = 'config'; return ['Enter configuration commands, one per line.  End with CNTL/Z.']; } },
  { pattern: 'copy running-config startup-config', run: (s) => ({ lines: [], ask: { prompt: 'Destination filename [startup-config]? ', answer: () => s.saveConfig() } }) },
  { pattern: 'write memory', run: (s) => s.saveConfig() },
  { pattern: 'write', run: (s) => s.saveConfig() },
  { pattern: 'clear counters', run: (s) => ({ lines: [], ask: { prompt: 'Clear "show interface" counters on all interfaces [confirm]', answer: (ans) => { if (/^n/i.test(ans.trim())) return []; Object.values(s.d.interfaces).forEach((i) => { i.counters = { inPkts: 0, outPkts: 0, crc: 0, inErrors: 0 }; }); return []; } } }) },
  ...K(['switch'], [
    { pattern: 'clear mac address-table dynamic', run: (s) => { s.d.macTable = []; return []; } },
  ]),
  ...K(['router'], [
    { pattern: 'clear ip nat translation <WORD>', run: (s, [w]) => { if (w !== '*') return s.invalidAt(w); s.d.nat.translations = []; return []; } },
    { pattern: 'clear arp-cache', run: (s) => { s.d.arp = {}; return []; } },
  ]),
  ...R_ROUTING_PRIV,
  ...SVC_PRIV,
  ...SEC_PRIV,
  ...MGMT_PRIV,
];

const CFG_EXITS = [
  { pattern: 'end', run: (s) => s.endConfig() },
  { pattern: 'do <LINE>', run: (s, [l]) => s.runDo(l) },
];
const CONFIG = [
  ...CFG_EXITS,
  { pattern: 'exit', run: (s) => s.endConfig() },
  { pattern: 'hostname <WORD>', run: (s, [h]) => { s.d.hostname = h; return []; } },
  { pattern: 'interface <IFC>', run: (s, [i]) => s.enterIf(i) },
  { pattern: 'interface range <IFRANGE>', run: (s, [r]) => s.enterRange(r) },
  ...SW_CONFIG,
  ...R_ROUTING_CONFIG,
  ...WAN_CONFIG,
  ...SVC_CONFIG,
  ...SEC_CONFIG,
  ...MGMT_CONFIG,
  { pattern: 'ip domain-lookup', run: (s) => { s.d.domainLookup = true; return []; } },
  { pattern: 'no ip domain-lookup', run: (s) => { s.d.domainLookup = false; return []; } },
  ...K(['switch'], [
    { pattern: 'vlan <1-4094>', run: (s, [v]) => s.enterVlan(+v) },
    { pattern: 'no vlan <1-4094>', run: (s, [v]) => { if (+v === 1) return ['Default VLAN 1 may not be deleted.']; delete s.d.vlans[+v]; return []; } },
  ]),
  ...K(['router'], [
    { pattern: 'ip route <A.B.C.D> <A.B.C.D> <A.B.C.D>', run: (s, [n, m, h]) => s.addRoute(n, m, { nh: h }) },
    { pattern: 'ip route <A.B.C.D> <A.B.C.D> <A.B.C.D> <1-255>', run: (s, [n, m, h, ad]) => s.addRoute(n, m, { nh: h, ad: +ad }) },
    { pattern: 'ip route <A.B.C.D> <A.B.C.D> <IF>', run: (s, [n, m, i]) => s.addRoute(n, m, { ifc: s.ifn(i) }) },
    { pattern: 'no ip route <A.B.C.D> <A.B.C.D> <A.B.C.D>', run: (s, [n, m, h]) => s.delRoute(n, m, { nh: h }) },
    { pattern: 'no ip route <A.B.C.D> <A.B.C.D> <IF>', run: (s, [n, m, i]) => s.delRoute(n, m, { ifc: s.ifn(i) }) },
    { pattern: 'no ip route <A.B.C.D> <A.B.C.D>', run: (s, [n, m]) => s.delRoute(n, m, {}) },
    { pattern: 'ipv6 unicast-routing', run: (s) => { s.d.ipv6Routing = true; return []; } },
    { pattern: 'no ipv6 unicast-routing', run: (s) => { s.d.ipv6Routing = false; return []; } },
    { pattern: 'ip dhcp pool <WORD>', run: (s, [p]) => { s.d.dhcp.pools[p] = s.d.dhcp.pools[p] || { name: p, dns: [] }; s.state.pool = p; s.state.mode = 'config-dhcp'; return []; } },
    { pattern: 'no ip dhcp pool <WORD>', run: (s, [p]) => { delete s.d.dhcp.pools[p]; return []; } },
    { pattern: 'ip dhcp excluded-address <A.B.C.D>', run: (s, [a]) => { s.d.dhcp.excluded.push([a]); return []; } },
    { pattern: 'ip dhcp excluded-address <A.B.C.D> <A.B.C.D>', run: (s, [a, b]) => { s.d.dhcp.excluded.push([a, b]); return []; } },
    { pattern: 'ip nat inside source list <1-99> interface <IF> overload', run: (s, [n, i]) => s.natRule(n, s.ifn(i), true) },
    { pattern: 'no ip nat inside source list <1-99> interface <IF> overload', run: (s, [n, i]) => s.natRule(n, s.ifn(i), false) },
  ]),
];

const IF_COMMON = [
  ...CFG_EXITS,
  { pattern: 'exit', run: (s) => { s.state.mode = 'config'; return []; } },
  { pattern: 'interface <IFC>', run: (s, [i]) => s.enterIf(i) },
  { pattern: 'interface range <IFRANGE>', run: (s, [r]) => s.enterRange(r) },
  { pattern: 'description <LINE>', run: (s, [t]) => s.eachIf((i) => { i.description = t; }) },
  { pattern: 'no description', run: (s) => s.eachIf((i) => { i.description = ''; }) },
  // shutdown also clears an err-disabled state (shutdown / no shutdown recovers a port-security violation)
  { pattern: 'shutdown', run: (s) => s.eachIf((i) => { i.shutdown = true; if (i.errDisabled) i.errDisabled = null; if (i.psec) i.psec.macs = i.psec.macs.filter((m) => m.type !== 'dynamic'); }) },
  { pattern: 'no shutdown', run: (s) => s.eachIf((i) => { i.shutdown = false; }) },
  ...['auto', '10', '100', '1000'].map((v) => ({ pattern: `speed ${v}`, run: (s) => s.eachIf((i) => { i.speed = v; }) })),
  ...['auto', 'full', 'half'].map((v) => ({ pattern: `duplex ${v}`, run: (s) => s.eachIf((i) => { i.duplex = v; }) })),
];
const IF_SWITCH = K(['switch'], [
  { pattern: 'switchport mode access', run: (s) => s.eachIf((i) => { i.mode = 'access'; i.explicitAccess = true; }) },
  { pattern: 'switchport mode trunk', run: (s) => s.trunkMode() },
  { pattern: 'switchport access vlan <1-4094>', run: (s, [v]) => s.accessVlan(+v) },
  { pattern: 'no switchport access vlan', run: (s) => s.eachIf((i) => { i.accessVlan = 1; }) },
  { pattern: 'switchport trunk native vlan <1-4094>', run: (s, [v]) => s.eachIf((i) => { i.nativeVlan = +v; }) },
]);
const IF_ROUTER = K(['router'], [
  { pattern: 'ip address <A.B.C.D> <A.B.C.D>', run: (s, [a, m]) => s.ipAddress(a, m) },
  { pattern: 'no ip address', run: (s) => s.eachIf((i) => { i.ip = null; }) },
  { pattern: 'ip nat inside', run: (s) => s.eachIf((i) => { i.nat = 'inside'; }) },
  { pattern: 'ip nat outside', run: (s) => s.eachIf((i) => { i.nat = 'outside'; }) },
  { pattern: 'no ip nat inside', run: (s) => s.eachIf((i) => { if (i.nat === 'inside') i.nat = null; }) },
  { pattern: 'no ip nat outside', run: (s) => s.eachIf((i) => { if (i.nat === 'outside') i.nat = null; }) },
  { pattern: 'ip helper-address <A.B.C.D>', run: (s, [a]) => s.eachIf((i) => { i.helper = a; }) },
  { pattern: 'ipv6 address <IPV6PFX>', run: (s, [p]) => s.ipv6Address(p) },
  { pattern: 'ipv6 address <WORD> link-local', run: (s, [a]) => s.ipv6LinkLocal(a) },
  { pattern: 'no ipv6 address', run: (s) => s.eachIf((i) => { i.ipv6 = []; }) },
  { pattern: 'ipv6 enable', run: (s) => s.eachIf((i) => { i.ipv6Enabled = true; }) },
]);
const VLAN = [
  ...CFG_EXITS,
  { pattern: 'exit', run: (s) => { s.state.mode = 'config'; return []; } },
  { pattern: 'name <WORD>', run: (s, [n]) => { s.d.vlans[s.state.vlan].name = n; return []; } },
];
const DHCP = [
  ...CFG_EXITS,
  { pattern: 'exit', run: (s) => { s.state.mode = 'config'; return []; } },
  { pattern: 'network <A.B.C.D> <A.B.C.D>', run: (s, [n, m]) => { if (maskToPrefix(m) === null) return ['% Invalid mask']; Object.assign(s.pool(), { network: networkOf(n, m), mask: m }); return []; } },
  { pattern: 'default-router <A.B.C.D>', run: (s, [a]) => { s.pool().router = a; return []; } },
  { pattern: 'dns-server <A.B.C.D>', run: (s, [a]) => { s.pool().dns = [a]; return []; } },
  { pattern: 'dns-server <A.B.C.D> <A.B.C.D>', run: (s, [a, b]) => { s.pool().dns = [a, b]; return []; } },
  ...DHCP_POOL_EXTRA,
];

const MODE_SPECS = {
  user: USER, priv: PRIV, config: CONFIG,
  'config-if': [...IF_COMMON, ...IF_SWITCH, ...SW_IF, ...IF_ROUTER, ...R_IF, ...R_ROUTING_IF, ...WAN_IF, ...SVC_IF, ...SEC_IF_ROUTER, ...SEC_IF_SWITCH, ...MGMT_IF],
  ...ROUTER_MODE_SPECS,
  ...WAN_MODE_SPECS,
  'config-if-range': [...IF_COMMON, ...IF_SWITCH, ...SW_IF.filter((x) => !x.pattern.includes('ip address')), ...SEC_IF_SWITCH],
  'config-vlan': VLAN, 'config-dhcp': DHCP,
  'config-line': SEC_LINE, 'config-std-nacl': SEC_STD_NACL, 'config-ext-nacl': SEC_EXT_NACL,
  'config-radius-server': SEC_RADIUS, 'config-server-tacacs': SEC_TACACS,
  rommon: ROMMON,
};

// Compile one trie per (mode, device kind), cached.
const tries = {};
function trieFor(mode, kind) {
  const k = `${mode}|${kind}`;
  if (!tries[k]) tries[k] = compile(MODE_SPECS[mode].filter((x) => !x.kinds || x.kinds.includes(kind)));
  return tries[k];
}

// ---------------------------------------------------------------- session
export class IosSession {
  /** state is persisted by the lab runtime: { mode, ifs, vlan, pool, history } */
  constructor(net, id, state) {
    this.net = net;
    this.id = id;
    this.state = state || IosSession.freshState();
  }
  static freshState() { return { mode: 'user', ifs: [], vlan: null, pool: null, history: [] }; }
  get d() { return dev(this.net, this.id); }
  bind(net) { this.net = net; }

  prompt() {
    const p = this.state.pendingPrompt;
    if (p != null && this.state.ask) return p;
    if (this.d.sys?.rommon) return `rommon ${this.d.sys.rommon.n} > `;
    if (this.state.locked) return '';
    return this.d.hostname + MODE_SUFFIX[this.state.mode];
  }

  ifn(raw) { return parseIfName(raw, this.d); }
  isSecret() { return !!(this.state.ask && this.state.secret); }

  /** Execute one line. Returns { lines:[{text, delay?}], event } */
  exec(rawLine) {
    const line = rawLine.replace(/\t/g, ' ');
    const promptBefore = this.prompt();
    // Interactive question pending (e.g. copy run start)?
    if (this.state.ask) {
      const ask = this.state.ask;
      const secret = !!this.state.secret;
      this.state.ask = null;
      this.state.pendingPrompt = null;
      this.state.secret = false;
      const fn = this.asks?.[ask];
      let out = fn ? fn(line) : [];
      let ok = true;
      if (out && !Array.isArray(out)) {
        if (out.ask) this.setAsk(ask, out.ask);
        if (out.ok === false) ok = false;
        out = out.lines || [];
      }
      return this.wrap(out, { line: secret ? '' : line, key: this.state.ask ? `${ask} (awaiting input)` : ask, ok, promptBefore, secret });
    }
    if (this.d.sys?.rommon) return this.romExec(line, promptBefore);
    if (this.state.locked) return this.wrap(this.loginStart(), { line: '', key: 'console login prompt', ok: true, promptBefore });
    if (!line.trim()) return this.wrap(this.flushConsole(), { line, key: '', ok: true, promptBefore, empty: true });
    this.state.history.push(line.trim());
    if (this.state.history.length > 50) this.state.history.shift();
    this.net.ticks = (this.net.ticks || 0) + 1;

    const before = this.snapshotLinks();
    const adjBefore = adjSnapshot(this.net, this.id);
    const fhrpBefore = fhrpSnapshot(this.net, this.id);
    const r = this.run(line);
    const logs = [...this.linkLogs(before), ...adjLogs(adjBefore, adjSnapshot(this.net, this.id)), ...fhrpLogs(fhrpBefore, fhrpSnapshot(this.net, this.id))];
    // Devices that keep a log buffer (Domain 05 labs) store every console message.
    if (this.d.kind === 'router' && (this.d.logging || this.d.logs)) {
      for (const l of [...(r.lines || []), ...logs]) { const t = typeof l === 'string' ? l : l.text; if (/^%[A-Z0-9_]+-\d-/.test(t)) logEvent(this.net, this.id, t); }
    }
    const lines = [...this.flushConsole(), ...(r.lines || []).map((t) => (typeof t === 'string' ? { text: t } : t)), ...logs];
    return { lines, event: { device: this.id, mode: r.mode, line: line.trim(), key: r.key || '', args: r.args || [], ok: !r.error, error: r.error || null, output: lines.map((l) => l.text).join('\n'), promptBefore } };
  }

  wrap(out, ev) {
    const lines = out.map((t) => (typeof t === 'string' ? { text: t } : t));
    return { lines, event: { device: this.id, output: lines.map((l) => l.text).join('\n'), args: [], ...ev } };
  }

  /** Core dispatcher with pipe filters, mode fallback and IOS error messages. */
  run(line, modeOverride) {
    const mode = modeOverride || this.state.mode;
    let cmdPart = line, pipe = null;
    const pi = line.indexOf('|');
    if (pi > -1) { cmdPart = line.slice(0, pi); pipe = line.slice(pi + 1).trim(); }
    const kind = this.d.kind;
    let m = match(trieFor(mode, kind), cmdPart, { device: this.d });
    // Sub-config modes fall back to global config (IOS behaviour).
    let usedMode = mode;
    if (!m.ok && mode.startsWith('config-')) {
      const g = match(trieFor('config', kind), cmdPart, { device: this.d });
      if (g.ok && g.cmd.pattern !== 'exit') { m = g; usedMode = 'config'; this.state.mode = 'config'; }
    }
    if (!m.ok) return { ...this.errorFor(m, cmdPart, mode), mode };
    if (pipe && !m.cmd.pattern.startsWith('show')) return { lines: this.caret(pi, 'invalid'), error: 'invalid', mode };
    let res = m.cmd.run(this, m.args);
    let key = m.cmd.pattern;
    if (res && !Array.isArray(res) && res.ask) {
      this.setAsk(m.cmd.pattern, res.ask);
      key = `${m.cmd.pattern} (awaiting confirmation)`; // the confirmed answer emits the real key
      res = res.lines;
    }
    let lines = Array.isArray(res) ? res : res?.lines || [];
    if (pipe) {
      const f = applyPipe(lines, pipe);
      if (f.error) return { lines: this.caret(pi + 1 + (line.slice(pi + 1).length - line.slice(pi + 1).trimStart().length), 'invalid'), error: 'invalid', mode };
      lines = f.lines;
    }
    return { lines, key, args: m.args, mode: usedMode };
  }

  errorFor(m, line, mode) {
    const promptLen = this.prompt().length;
    if (m.error === 'ambiguous') return { lines: [`% Ambiguous command:  "${line.trim()}"`], error: 'ambiguous' };
    if (m.error === 'incomplete') return { lines: ['% Incomplete command.', ''], error: 'incomplete' };
    // Unknown first word in EXEC → IOS tries to resolve it as a hostname (telnet).
    if ((mode === 'user' || mode === 'priv') && m.depth === 0 && tokenize(line).length === 1) {
      const w = line.trim();
      return {
        lines: this.d.domainLookup
          ? [{ text: `Translating "${w}"...domain server (255.255.255.255)` }, { text: '% Unknown command or computer name, or unable to find computer address', delay: 900 }]
          : ['% Unknown command or computer name, or unable to find computer address'],
        error: 'unknown',
      };
    }
    return { lines: this.caret(m.col, 'invalid', promptLen), error: 'invalid' };
  }

  caret(col, _type, promptLen = this.prompt().length) {
    return [`${' '.repeat(promptLen + col)}^`, "% Invalid input detected at '^' marker.", ''];
  }
  invalidAt() { return ["% Invalid input detected at '^' marker."]; }

  // ------------------------------------------------ mode helpers
  logout() {
    this.state.mode = 'user';
    this.state.loginUser = null;
    // A console line with a login method asks for credentials on the next RETURN.
    if (loginPrompt(this.d, 'con') !== 'none') this.state.locked = true;
    return [{ text: '' }, { text: `${this.d.hostname} con0 is now available` }, { text: '' }, { text: 'Press RETURN to get started.' }];
  }
  setAsk(key, ask) {
    this.asks = { ...(this.asks || {}), [key]: ask.answer };
    this.state.ask = key;
    this.state.pendingPrompt = ask.prompt;
    this.state.secret = !!ask.secret;
  }
  /** Unsolicited console messages queued by the device (e.g. a port-security violation). */
  flushConsole() {
    const q = this.d.consoleQueue;
    if (!q?.length) return [];
    this.d.consoleQueue = [];
    return q.map((t) => ({ text: t }));
  }
  /** Privileged EXEC: asks for the enable secret / password when one is configured. */
  enable() {
    const m = mgmtOf(this.d);
    const want = m.enableSecret || m.enablePassword;
    if (!want) { this.state.mode = 'priv'; return []; }
    let tries = 0;
    const ask = { prompt: 'Password: ', secret: true, answer: (pw) => {
      if (pwMatches(want, pw)) { this.state.mode = 'priv'; return []; }
      tries++;
      if (tries >= 3) return { lines: ['% Bad secrets', ''], ok: false };
      return { lines: [], ask, ok: false };
    } };
    return { lines: [], ask };
  }
  /** Console login after logout (line password, local users or AAA). */
  loginStart() {
    const d = this.d;
    const m = mgmtOf(d);
    const head = [...(m.banner != null ? m.banner.split('\n') : []), '', 'User Access Verification', ''];
    const kind = loginPrompt(d, 'con');
    if (kind === 'none') { this.state.locked = false; return []; }
    let tries = 0;
    const attempt = (user, pass) => {
      const r = authenticate(this.net, this.id, { line: 'con', user, pass, src: 'console', record: true }, aaaDeps);
      if (r.ok) {
        this.state.locked = false;
        this.state.loginUser = user || null;
        this.state.mode = r.priv >= 15 ? 'priv' : 'user';
        return { lines: [''] };
      }
      tries++;
      if (r.reason === 'no-password-set') { this.state.locked = false; return { lines: [''] }; } // console without a password: IOS lets you in
      if (tries >= 3) return { lines: [kind === 'password' ? '% Bad passwords' : '% Authentication failed', ''], ok: false };
      return { lines: kind === 'password' ? [] : ['% Login invalid', ''], ask: kind === 'password' ? passAsk(null) : userAsk, ok: false };
    };
    const passAsk = (user) => ({ prompt: 'Password: ', secret: true, answer: (pw) => attempt(user, pw) });
    const userAsk = { prompt: 'Username: ', answer: (u) => ({ lines: [], ask: passAsk(u.trim()) }) };
    this.setAsk('console login', kind === 'password' ? passAsk(null) : userAsk);
    return head;
  }
  testAaa(proto, user, pass) {
    const r = testAaa(this.net, this.id, proto, user, pass);
    const head = `Attempting authentication test to server-group ${proto === 'radius' ? 'radius' : 'tacacs+'} using ${proto === 'radius' ? 'radius' : 'tacacs+'}`;
    if (r.result === 'none') return ['% No servers found in server-group, or AAA is not enabled.'];
    if (r.result === 'accept') return [head, { text: 'User was successfully authenticated.', delay: 500 }];
    if (r.result === 'reject') return [head, { text: 'User authentication request was rejected by server.', delay: 500 }];
    return [head, { text: 'No authoritative response from any server.', delay: 2000 }];
  }
  endConfig() {
    this.state.mode = 'priv';
    this.state.ifs = [];
    this.state.router = null;
    this.state.cmap = this.state.pmap = this.state.pclass = null;
    this.state.line = this.state.acl = this.state.aaaSrv = null;
    return [{ text: `%SYS-5-CONFIG_I: Configured from console by ${this.state.loginUser || 'console'}`, delay: 150 }];
  }
  runDo(l) {
    const r = this.run(l, 'priv');
    if (this.state.mode === 'priv') this.state.mode = 'config';
    return r.lines;
  }
  enterIf(raw) {
    const n = this.ifn(raw) || parseIfNameCreate(raw, this.d);
    createInterface(this.d, n); // SVIs, Port-channels and subinterfaces are created on first use
    this.state.ifs = [n];
    this.state.mode = 'config-if';
    return [];
  }
  enterRange(text) {
    const list = parseIfRange(text, this.d);
    if (!list || this.d.kind !== 'switch') return this.caret(16, 'invalid');
    this.state.ifs = list;
    this.state.mode = 'config-if-range';
    return [];
  }
  enterVlan(v) {
    if (v >= 1002 && v <= 1005) return ['Default VLAN ' + v + ' may not be modified.'];
    if (!this.d.vlans[v]) this.d.vlans[v] = { name: `VLAN${String(v).padStart(4, '0')}` };
    this.state.vlan = v;
    this.state.mode = 'config-vlan';
    return [];
  }
  pool() { return this.d.dhcp.pools[this.state.pool]; }
  eachIf(fn) {
    for (const n of this.state.ifs) {
      fn(this.d.interfaces[n], n);
      // Configuration on a Port-channel is propagated to its member ports (IOS behaviour).
      if (isPo(n)) {
        const id = +n.replace('Port-channel', '');
        for (const [mn, m] of Object.entries(this.d.interfaces)) if (m.channelGroup?.id === id) fn(m, mn);
      }
    }
    return [];
  }
  trunkMode() {
    for (const n of this.state.ifs) {
      const i = this.d.interfaces[n];
      if (this.d.l3 && i.trunkEncap !== 'dot1q') return ['Command rejected: An interface whose trunk encapsulation is "Auto" can not be configured to "trunk" mode.'];
    }
    return this.eachIf((i) => { i.mode = 'trunk'; });
  }
  encap(v, nat) {
    const n = this.state.ifs[0];
    if (!isSubif(n)) return ["% Invalid input detected at '^' marker."];
    const clash = Object.entries(this.d.interfaces).find(([o, i]) => o !== n && o.split('.')[0] === n.split('.')[0] && i.encap?.vlan === v);
    if (clash) return [`Configuration of multiple subinterfaces of the same main interface with the same VID (${v}) is not permitted. This VID is already configured on ${clash[0]}.`];
    this.d.interfaces[n].encap = { vlan: v, native: nat };
    return [];
  }

  accessVlan(v) {
    const out = [];
    if (!this.d.vlans[v]) {
      this.d.vlans[v] = { name: `VLAN${String(v).padStart(4, '0')}` };
      out.push('% Access VLAN does not exist. Creating vlan ' + v);
    }
    this.eachIf((i) => { i.accessVlan = v; });
    return out;
  }

  ipAddress(a, m) {
    const p = maskToPrefix(m);
    const hex = '0x' + (ipToInt(m) >>> 0).toString(16).toUpperCase().padStart(8, '0');
    if (p === null || p === 0) return [`Bad mask ${hex} for address ${a}`];
    if (!isHostAddress(a, m)) return [`Bad mask /${p} for address ${a}`];
    const n = this.state.ifs[0];
    if (isSubif(n) && !this.d.interfaces[n].encap) {
      return ['% Configuring IP routing on a LAN subinterface is only allowed if that', 'subinterface is already configured as part of an IEEE 802.10, IEEE 802.1Q,', 'or ISL vLAN.', ''];
    }
    for (const [other, i] of Object.entries(this.d.interfaces)) {
      if (other === n || !i.ip) continue;
      const shorter = Math.min(p, maskToPrefix(i.ip.mask));
      if (networkOf(a, shorter) === networkOf(i.ip.ip, shorter)) {
        return [`% ${networkOf(a, m)} overlaps with ${other}`];
      }
    }
    this.d.interfaces[n].ip = { ip: a, mask: m };
    return [];
  }
  ipv6Address(pfx) {
    const [a, len] = pfx.split('/');
    if (!v6Expand(a) || +len > 128) return this.caret(13, 'invalid');
    const i = this.d.interfaces[this.state.ifs[0]];
    i.ipv6 = i.ipv6.filter((x) => x.addr.startsWith('fe80'));
    i.ipv6.push({ addr: v6Compress(a), prefix: +len });
    i.ipv6Enabled = true;
    return [];
  }
  ipv6LinkLocal(a) {
    if (!v6Expand(a) || !v6Compress(a).startsWith('fe80')) return ['% Invalid link-local address'];
    const i = this.d.interfaces[this.state.ifs[0]];
    i.ipv6 = i.ipv6.filter((x) => !x.addr.startsWith('fe80'));
    i.ipv6.push({ addr: v6Compress(a), prefix: 64 });
    i.ipv6Enabled = true;
    return [];
  }
  addRoute(n, m, { nh, ifc, ad }) {
    const p = maskToPrefix(m);
    if (p === null) return ['%Inconsistent address and mask'];
    if (networkOf(n, m) !== n) return ['%Inconsistent address and mask'];
    if (nh && Object.values(this.d.interfaces).some((i) => i.ip?.ip === nh)) return ['%Invalid next hop address (it\'s this router)'];
    const exists = this.d.routes.find((r) => r.net === n && r.mask === m && r.nh === (nh || undefined) && r.ifc === (ifc || undefined));
    if (exists) { if (ad) exists.ad = ad; return []; }
    this.d.routes.push({ net: n, mask: m, ...(nh ? { nh } : {}), ...(ifc ? { ifc } : {}), ...(ad ? { ad } : {}) });
    return [];
  }
  delRoute(n, m, { nh, ifc }) {
    const before = this.d.routes.length;
    this.d.routes = this.d.routes.filter((r) => !(r.net === n && r.mask === m && (!nh || r.nh === nh) && (!ifc || r.ifc === ifc)));
    return before === this.d.routes.length ? ['%No matching route to delete'] : [];
  }
  acl(n, action, base, wild, text) {
    (this.d.acls[n] = this.d.acls[n] || []).push({ action, base, wild, text: text || `${base} ${wild}` });
    return [];
  }
  natRule(acl, ifc, add) {
    this.d.nat.rules = this.d.nat.rules.filter((r) => !(r.acl === acl && r.ifc === ifc));
    if (add) this.d.nat.rules.push({ acl, ifc, overload: true });
    return [];
  }
  // ------------------------------------------------ Domain 07: ROM monitor, replay, reload
  romExec(line, promptBefore) {
    this.state.mode = 'rommon';
    if (!line.trim()) return this.wrap([], { line, key: '', ok: true, promptBefore, empty: true });
    this.state.history.push(line.trim());
    const m = match(trieFor('rommon', this.d.kind), line, { device: this.d });
    const n = this.d.sys.rommon;
    if (!m.ok) { n.n++; return this.wrap([`monitor: command "${line.trim().split(/\s+/)[0]}" not found`], { line: line.trim(), key: '', ok: false, error: 'invalid', promptBefore, mode: 'rommon' }); }
    const out = m.cmd.run(this, m.args);
    if (this.d.sys.rommon) this.d.sys.rommon.n++;
    return this.wrap(out, { line: line.trim(), key: m.cmd.pattern, args: m.args, ok: true, promptBefore, mode: 'rommon' });
  }
  /** Feed saved configuration text through the parser (merge). Returns error lines. */
  replayLines(lines, { boot = false } = {}) {
    const ticks = this.net.ticks;
    const sub = new IosSession(this.net, this.id, { ...IosSession.freshState(), mode: 'config' });
    const errs = [];
    const text = lines.flatMap((l) => String(typeof l === 'string' ? l : l.text).split('\n'));
    const upIfs = new Set();
    let cur = null, curShut = false;
    const closeIf = () => { if (cur && !curShut) upIfs.add(cur); cur = null; };
    for (const raw of text) {
      if (sub.state.ask) { sub.exec(raw); continue; }
      if (/^(Building configuration|Current configuration|Using \d+ out of)/.test(raw) || /^!/.test(raw) || raw.trim() === 'end' || !raw.trim()) continue;
      if (!raw.startsWith(' ')) { closeIf(); sub.state.mode = 'config'; sub.state.ifs = []; sub.state.line = null; }
      const m = raw.match(/^interface (\S+)/);
      if (m) { cur = m[1]; curShut = false; }
      if (cur && raw === ' shutdown') curShut = true;
      const r = sub.exec(raw.trim());
      if (r.event.ok === false && r.event.error === 'invalid') errs.push(`% Invalid input detected at '^' marker. (${raw.trim()})`);
    }
    closeIf();
    // At boot, an interface listed without "shutdown" comes up (a merge keeps the current state).
    if (boot) for (const n of upIfs) if (this.d.interfaces[n]) this.d.interfaces[n].shutdown = false;
    this.net.ticks = ticks;
    return errs;
  }
  /** Rebuild the device from its hardware state, optionally replaying configuration text. */
  rebuild(lines) {
    const old = this.d;
    const fresh = JSON.parse(old.sys.factory);
    fresh.sys = old.sys;
    // boot statements and CDP/LLDP are configuration (they come back only from the replayed text);
    // the configuration register, flash and the running image are not.
    Object.assign(fresh.sys, { boot: [], cdp: true, lldp: false });
    fresh.startup = old.startup;
    fresh.startupState = old.startupState ?? null;
    for (const k of ['clockSkewMs', 'serial', 'platform']) if (old[k] != null) fresh[k] = old[k];
    fresh.sentLogs = [...(old.sentLogs || []), ...(old.logs || []).filter((e) => e.to?.length)].slice(-120);
    fresh.logs = [];
    this.net.devices[this.id] = fresh;
    if (lines) this.replayLines(lines, { boot: true });
    if (old.mgmt?.rsa) ensureMgmt(this.d).rsa = old.mgmt.rsa;
    return this.d;
  }
  /** Back to a fresh console (keeps history); mutates in place so the persisted state follows. */
  resetState(extra = {}) {
    const history = this.state.history;
    for (const k of Object.keys(this.state)) delete this.state[k];
    Object.assign(this.state, IosSession.freshState(), { history }, extra);
  }
  /** configure replace: running-config becomes exactly the file (no reboot). */
  replaceConfig(lines) {
    this.rebuild(lines);
    this.state.mode = 'priv';
  }
  /** Reload (or ROMMON boot): choose an image, then load startup-config unless the register says not to. */
  reloadDevice({ file = null } = {}) {
    const old = this.d;
    const sys = old.sys;
    const fromIos = !sys.rommon;
    const out = fromIos ? ['', '%SYS-5-RELOAD: Reload requested by console. Reload Reason: Reload Command.', ''] : [''];
    if (sys.confregNext) { sys.confreg = sys.confregNext; sys.confregNext = null; }
    const ch = file ? { image: file, messages: [] } : chooseImage(old);
    out.push('System Bootstrap, Version 15.0(1r)M15, RELEASE SOFTWARE (fc1)', 'Copyright (c) 2012 by cisco Systems, Inc.', ...ch.messages);
    old.arp = {}; if (old.macTable) old.macTable = [];
    if (ch.rommon) {
      sys.rommon = { n: 1 };
      this.resetState({ mode: 'rommon' });
      out.push('', 'rommon 1 > ');
      return out.slice(0, -1);
    }
    sys.rommon = false;
    sys.image = { file: ch.image.name, version: ch.image.image.version, platform: ch.image.image.platform };
    sys.reloads = (sys.reloads || 0) + 1;
    sys.bootedAt = this.net.ticks || 0;
    out.push('', `program load complete, entry point: 0x80803000, size: 0x${ch.image.size.toString(16)}`, `Self decompressing the image : ${'#'.repeat(40)} [OK]`, '',
      `Cisco IOS Software, ${old.kind === 'switch' ? 'C2960 Software (C2960-LANBASEK9-M)' : 'C2900 Software (C2900-UNIVERSALK9-M)'}, Version ${sys.image.version}, RELEASE SOFTWARE`, '');
    const ignore = ignoresStartup(sys.confreg);
    const startup = ignore ? null : old.startup;
    this.rebuild(startup);
    logEvent(this.net, this.id, `%SYS-5-RESTART: System restarted --`);
    logEvent(this.net, this.id, `%SYS-6-BOOTTIME: Time taken to reboot after reload = 194 seconds`);
    this.resetState();
    if (!startup) {
      out.push(ignore ? '[Lab] Configuration register ignores NVRAM: startup-config not loaded.' : '[Lab] startup-config is not present: the device starts unconfigured.', '', '         --- System Configuration Dialog ---', '');
      this.setAsk('setup dialog', { prompt: 'Would you like to enter the initial configuration dialog? [yes/no]: ', answer: (a) => ({ lines: [...(/^y/i.test(a.trim()) ? ['[Lab] The setup dialog is not simulated — continuing without it.'] : []), '', 'Press RETURN to get started!', ''] }) });
      return out;
    }
    if (loginPrompt(this.d, 'con') !== 'none') this.state.locked = true;
    out.push('', 'Press RETURN to get started!', '');
    return out;
  }

  saveConfig() {
    this.d.startup = show.runningConfig(this.net, this.id);
    this.d.startupState = JSON.stringify({ ...this.d, startup: null, startupState: null });
    return ['Building configuration...', { text: '[OK]', delay: 400 }];
  }


  // ------------------------------------------------ ping / traceroute
  resolveHost(h) {
    if (isIp(h)) return { ip: h };
    return {
      lines: this.d.domainLookup
        ? [`Translating "${h}"...domain server (255.255.255.255)`, { text: '% Unrecognized host or address, or protocol not running.', delay: 900 }]
        : ['% Unrecognized host or address, or protocol not running.'],
    };
  }
  ping(h) {
    const r = this.resolveHost(h);
    if (!r.ip) return r.lines;
    const out = ['Type escape sequence to abort.', `Sending 5, 100-byte ICMP Echos to ${r.ip}, timeout is 2 seconds:`];
    let marks = '', ok = 0;
    const times = [];
    for (let k = 0; k < 5; k++) {
      const rt = roundTrip(this.net, this.id, r.ip, { ttl: 255 });
      if (rt.ok) {
        if (rt.firstArp && k === 0) { marks += '.'; continue; }
        if (rt.lossy && k % 2) { marks += '.'; continue; }
        marks += '!'; ok++; times.push(1 + rt.hops * 2 + (k % 2));
      } else marks += rt.unreachableFrom ? 'U' : '.';
    }
    out.push({ text: marks, delay: 300 * marks.length, progressive: true });
    const tail = ok
      ? `Success rate is ${ok * 20} percent (${ok}/5), round-trip min/avg/max = ${Math.min(...times)}/${Math.round(times.reduce((a, b) => a + b) / ok)}/${Math.max(...times)} ms`
      : 'Success rate is 0 percent (0/5)';
    out.push(tail);
    this.lastPing = { target: r.ip, success: ok > 0, ok };
    return out;
  }
  traceroute(h) {
    const r = this.resolveHost(h);
    if (!r.ip) return r.lines;
    const out = ['Type escape sequence to abort.', `Tracing the route to ${r.ip}`, 'VRF info: (vrf in name/id, vrf out name/id)'];
    const hops = trace(this.net, this.id, r.ip, 15);
    hops.forEach((x, k) => {
      const n = String(k + 1).padStart(3);
      out.push({ text: x.ip ? `${n} ${x.ip} ${1 + k} msec ${k} msec ${1 + k} msec` : `${n}  *  *  *`, delay: x.ip ? 250 : 900 });
    });
    return out;
  }

  // ------------------------------------------------ help / completion
  help(line) {
    const h = helpFor(trieFor(this.state.mode, this.d.kind), line, { device: this.d });
    if (h.error) return ['% Unrecognized command'];
    if (h.words) return h.words.length ? [h.words.join('  ')] : ['% Unrecognized command'];
    const w = Math.max(...h.rows.map((r) => r[0].length), 10) + 2;
    return h.rows.map(([k, d]) => `  ${k.padEnd(w)}${d}`);
  }
  complete(line) { return completeFor(trieFor(this.state.mode, this.d.kind), line, { device: this.d }); }

  // ------------------------------------------------ syslog on link changes
  snapshotLinks() {
    const s = {};
    for (const n of Object.keys(this.d.interfaces)) s[n] = ifStatus(this.net, this.id, n);
    return s;
  }
  linkLogs(before) {
    const out = [];
    for (const n of Object.keys(this.d.interfaces)) {
      const a = before[n] || { status: 'down', protocol: 'down' }, b = ifStatus(this.net, this.id, n); // new SVI / Port-channel
      if (a.status === b.status && a.protocol === b.protocol) continue;
      if (!before[n] && b.status === 'administratively down') continue; // newly created, still shut
      if (a.status === b.status) { // e.g. EtherChannel member suspended / resumed
        out.push({ text: `%LINEPROTO-5-UPDOWN: Line protocol on Interface ${n}, changed state to ${b.protocol}`, delay: 200, log: true });
        continue;
      }
      if (b.status === 'administratively down') {
        out.push({ text: `%LINK-5-CHANGED: Interface ${n}, changed state to administratively down`, delay: 200, log: true });
        if (a.protocol === 'up') out.push({ text: `%LINEPROTO-5-UPDOWN: Line protocol on Interface ${n}, changed state to down`, delay: 150, log: true });
      } else if (b.status === 'up') {
        out.push({ text: `%LINK-5-CHANGED: Interface ${n}, changed state to up`, delay: 400, log: true });
        out.push({ text: `%LINEPROTO-5-UPDOWN: Line protocol on Interface ${n}, changed state to up`, delay: 600, log: true });
      } else if (b.status === 'down' && a.status === 'administratively down') {
        out.push({ text: `%LINK-3-UPDOWN: Interface ${n}, changed state to down`, delay: 300, log: true });
      } else if (b.status === 'down') {
        out.push({ text: `%LINK-3-UPDOWN: Interface ${n}, changed state to down`, delay: 300, log: true });
        out.push({ text: `%LINEPROTO-5-UPDOWN: Line protocol on Interface ${n}, changed state to down`, delay: 150, log: true });
      }
    }
    return out;
  }
}

/** "| include X", "| exclude X", "| begin X", "| section X" */
export function applyPipe(lines, spec) {
  const m = spec.match(/^(\S+)\s+(.+)$/);
  if (!m) return { error: true };
  const op = ['include', 'exclude', 'begin', 'section'].filter((o) => o.startsWith(m[1].toLowerCase()));
  if (op.length !== 1) return { error: true };
  let re;
  try { re = new RegExp(m[2]); } catch { re = { test: (s) => s.includes(m[2]) }; }
  const text = lines.map((l) => (typeof l === 'string' ? l : l.text));
  if (op[0] === 'include') return { lines: text.filter((l) => re.test(l)) };
  if (op[0] === 'exclude') return { lines: text.filter((l) => !re.test(l)) };
  if (op[0] === 'begin') { const i = text.findIndex((l) => re.test(l)); return { lines: i < 0 ? [] : text.slice(i) }; }
  const out = [];
  let inSec = false;
  for (const l of text) {
    if (!l.startsWith(' ')) inSec = re.test(l);
    if (inSec) out.push(l);
  }
  return { lines: out };
}

/** Console banner shown when a learner first opens a device. */
export function banner(d) {
  return [`${d.hostname} con0 is now available`, '', 'Press RETURN to get started.', ''];
}
export { isHost };
